import { useState, useEffect, memo, useCallback, useMemo } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import axios from 'axios'; // Import axios normally - it's not that heavy
import SEOHead from '../components/SEOHead'; 

// Lazy load heavy dependencies
const toast = dynamic(() => import('react-hot-toast').then(mod => ({ default: mod.toast })), { ssr: false }); 

import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext'; 
import { useAuth } from '../hooks/useAuth'; 
import { LoadingPage } from '../components/ui/LoadingPage'; 
import Button from '../components/ui/Button';
import { ProductCardSkeleton, CategoryCardSkeleton, FeatureCardSkeleton, HeroSkeleton } from '../components/ui/Skeleton';
import ProductCard from '../components/product/ProductCard'; 

// Optimized icon imports using single import
import { 
 ShoppingCartIcon,
 TruckIcon,
 ShieldCheckIcon,
 StarIcon,
 ArrowRightIcon,
 ShoppingBagIcon
} from '@heroicons/react/24/outline'; 

// --- Helper Components (Defined outside the main Home component) ---

// Category Card Component - Optimized with React.memo
const CategoryCard = memo(({ category, index, fallbackImages: adminImages }) => {
 const router = useRouter();

 const handleCategoryClick = useCallback(() => {
 router.push(`/products/category/${category.slug}`);
 }, [router, category.slug]);

 // Asymmetric aspect ratios based on index
 const aspectClass = index % 3 === 0 ? 'aspect-[4/5]' : 'aspect-square';

 const localDefaults = ['/cat_serigraphie.png', '/cat_clothes.png', '/cat_accessories.png', '/cat_lifestyle.png'];
 // Admin-set gallery image takes priority over any category image from the database
 const adminImage = adminImages?.[index % 4] || '';
 const displayImage = adminImage || category.imageUrl || category.iconUrl || localDefaults[index % localDefaults.length];

 return (
 <div
 className={`group relative flex flex-col overflow-hidden bg-theme-primary cursor-pointer transition-all duration-1000 ${aspectClass}`}
 onClick={handleCategoryClick}
 >
 <div className="relative w-full h-full overflow-hidden">
 <Image 
 src={displayImage} 
 alt={category.name} 
 fill
 className="object-cover transition-transform duration-[2000ms] cubic-bezier(0.4, 0, 0.2, 1) group-hover:scale-110" 
 />
 
 {/* Minimalist Hover Overlay */}
 <div className="absolute inset-x-0 bottom-0 p-8 flex flex-col justify-end translate-y-4 group-hover:translate-y-0 transition-transform duration-700 ease-out">
 <div className="bg-theme-primary/80 backdrop-blur-md p-6 border border-theme shadow-2xl">
 <span className="text-[10px] text-accent font-bold tracking-[0.3em] uppercase mb-2 block">Collection {index + 1}</span>
 <h3 className="text-xl font-black text-accent mb-3 tracking-tight">
 {category.name}
 </h3>
 <div className="w-8 h-[1px] bg-accent transition-all duration-700 group-hover:w-full mb-4"></div>
 <p className="text-[10px] text-theme-secondary uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-opacity duration-1000 delay-300">
 Découvrir l'univers
 </p>
 </div>
 </div>
 </div>
 </div>
 );
});

CategoryCard.displayName = 'CategoryCard';

// --- Main Index Page Component ---
export default function Home() {
 const { mounted: themeMounted } = useTheme();
 const { loading: authLoading } = useAuth();

 const [featuredProducts, setFeaturedProducts] = useState([]);
 const [newArrivals, setNewArrivals] = useState([]);
 const [loadingData, setLoadingData] = useState(true);
 const [error, setError] = useState(null);
 const [categories, setCategories] = useState([]);
 const [siteContent, setSiteContent] = useState({
   hero: {
     badge: 'FenkParet Production',
     titleLine1: 'Exprimez votre',
     titleAccent: 'singularité',
     description: 'Découvrez nos créations personnalisées et produits artistiques. Des pièces uniques conçues pour raconter votre histoire.',
     button1: 'Explorer la collection',
     button2: 'Notre mission',
   },
   stats: [
     { value: '2k+', label: 'Membres' },
     { value: '100%', label: 'Artistique' },
     { value: '24h', label: 'Réponse' },
   ],
   features: [
     { title: 'Livraison gratuite', description: 'Livraison gratuite pour les commandes de plus de 10000 HTG' },
     { title: 'Paiement sécurisé', description: 'Paiement sûrs et sécurisés avec vérification administrative pour votre tranquillité.' },
     { title: 'Produits de qualité', description: 'Articles de qualité supérieure à des prix avantageux' },
   ],
   categories: {
     titlePrefix: 'Explorez par',
     titleAccent: 'catégories',
     subtitle: 'Trouvez votre préférences parmi nos sélections.',
   },
   featured: {
     titlePrefix: 'Nos coups de',
     titleAccent: 'cœur',
     description: 'Une sélection de nos pièces les plus emblématiques, choisies pour leur caractère et leur durabilité.',
   },
   gallery: {
     image1: '',
     image2: '',
     image3: '',
     image4: '',
   },
 });

 const apiBaseUrl = useMemo(() => 
 process.env.NEXT_PUBLIC_API_URL || 'https://us-central1-fenparet-website.cloudfunctions.net/api', 
 []
 );

 const fetchData = useCallback(async () => {
 setLoadingData(true);
 try {
 // Optimize: Make all API calls in parallel for faster loading
 const [featuredResponse, newArrivalsResponse, categoriesResponse, siteContentResponse] = await Promise.all([
 axios.get(`${apiBaseUrl}/products/featured?limit=4`),
 axios.get(`${apiBaseUrl}/products/new-arrivals?limit=2`),
 axios.get(`${apiBaseUrl}/products/categories`),
 axios.get(`${apiBaseUrl}/site-content`).catch(() => ({ data: { content: null } }))
 ]);

 const featuredData = featuredResponse.data.products || [];
 const newArrivalsData = newArrivalsResponse.data.products || [];
 const categoriesData = categoriesResponse.data.categories || [];

 if (siteContentResponse.data?.content) {
 setSiteContent(prev => ({ ...prev, ...siteContentResponse.data.content }));
 }

 // Transform categories to include slug and fetch product counts
 const transformedCategories = await Promise.all(
 categoriesData.map(async (cat) => {
 try {
 // Fetch products for this category to get count
 const categoryProductsResponse = await axios.get(
 `${apiBaseUrl}/products?category=${cat.value}&limit=1`
 );
 const productCount = categoryProductsResponse.data.pagination?.total || 0;

 return {
 _id: cat.value,
 name: cat.name,
 slug: cat.value, // Use exact value so category page can fetch directly
 productCount,
 iconUrl: cat.iconUrl || null
 };
 } catch (error) {
 // If fetching product count fails, return category with 0 count
 return {
 _id: cat.value,
 name: cat.name,
 slug: cat.value, // Use exact value so category page can fetch directly
 productCount: 0,
 iconUrl: cat.iconUrl || null
 };
 }
 })
 );

 setFeaturedProducts(featuredData);
 setNewArrivals(newArrivalsData);
 setCategories(transformedCategories);
 
 // Only show success message in development
 if (process.env.NODE_ENV === 'development') {
 console.log('Featured products:', featuredData);
 console.log('New arrivals:', newArrivalsData);
 console.log('Categories:', categoriesData);
 if (featuredData.length > 0 || newArrivalsData.length > 0 || categoriesData.length > 0) {
 console.log(' Data loaded successfully from server');
 }
 }
 
 } catch (error) {
 if (process.env.NODE_ENV === 'development') {
 console.error('Error fetching data:', error);
 }
 
 // Only show error toast for actual network/server errors
 if (error.response?.status === 429) {
 toast.error('Trop de requêtes. Utilisation des données en cache.');
 } else if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
 toast.error('Pas de connexion internet. Utilisation des données hors ligne.');
 } else if (error.response?.status >= 500) {
 toast.error('Serveur temporairement indisponible. Utilisation des données en cache.');
 }
 
 // Set demo data for development when API fails
 const demoProducts = [
 {
 _id: 'demo1',
 name: 'T-shirt Premium Homme',
 price: 2500,
 comparePrice: 3000,
 images: [{ url: '/placeholder.jpg' }],
 averageRating: 4.5,
 reviewCount: 23,
 shortDescription: 'T-shirt en coton premium avec un design moderne et confortable.',
 featured: true
 },
 {
 _id: 'demo2',
 name: 'Mug Personnalisé',
 price: 1200,
 images: [{ url: '/placeholder.jpg' }],
 averageRating: 4.8,
 reviewCount: 45,
 shortDescription: 'Mug en céramique de qualité supérieure, parfait pour vos boissons chaudes.',
 isNew: true
 },
 {
 _id: 'demo3',
 name: 'Accessoire Décoratif',
 price: 1800,
 images: [{ url: '/placeholder.jpg' }],
 averageRating: 4.2,
 reviewCount: 12,
 shortDescription: 'Bel accessoire décoratif pour embellir votre intérieur.',
 inventory: { stockStatus: 'low-stock' }
 }
 ];

 const demoCategories = [
 {
 _id: 'cat1',
 name: 'Vêtements',
 slug: 'vetements',
 productCount: 15,
 iconUrl: '/placeholder-image.svg'
 },
 {
 _id: 'cat2',
 name: 'Accessoires',
 slug: 'accessoires',
 productCount: 8,
 iconUrl: '/placeholder-image.svg'
 }
 ];

 setFeaturedProducts(demoProducts);
 setNewArrivals(demoProducts.slice(0, 2));
 setCategories(demoCategories);
 setError('Mode démo - Données d\'exemple affichées.');
 } finally {
 setLoadingData(false);
 }
 }, [apiBaseUrl]);

 useEffect(() => {
 if (themeMounted) { 
 fetchData(); // Don't wait for auth - fetch data immediately
 }
 }, [themeMounted, fetchData]);

 const featureIcons = useMemo(() => [TruckIcon, ShieldCheckIcon, StarIcon], []);

 // Raw admin values — empty string means "not set", so fall back to category image
 const galleryFallbacks = useMemo(() => [
   siteContent?.gallery?.image1 || '',
   siteContent?.gallery?.image2 || '',
   siteContent?.gallery?.image3 || '',
   siteContent?.gallery?.image4 || '',
 ], [siteContent?.gallery?.image1, siteContent?.gallery?.image2, siteContent?.gallery?.image3, siteContent?.gallery?.image4]);


 return (
 <>
 <SEOHead
 title="Bienvenue chez Fenkparet | Boutique en ligne Haïti"
 description="Votre marketplace de confiance pour des produits haïtiens de qualité. T-shirts personnalisés, mugs, accessoires et bien plus. Paiement sécurisé avec MonCash et livraison en Haïti."
 keywords="marketplace haïtien, produits de qualité, MonCash, livraison Haïti, t-shirts personnalisés, mugs, accessoires, e-commerce Haïti"
 />
 {/* Hero Section - Redesigned Organic Split Layout */}
 {loadingData ? (
 <HeroSkeleton />
 ) : (
 <section className="relative w-full min-h-[600px] lg:min-h-[700px] flex items-center overflow-hidden bg-bg-primary pt-16 lg:pt-0">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
 {/* Text Content */}
 <div className="flex flex-col space-y-8 z-10 text-center lg:text-left">
 <div className="w-fit mx-auto lg:mx-0 px-4 py-1.5 bg-accent-light text-accent rounded-full text-sm font-bold tracking-wide animate-fade-in uppercase">
 {siteContent.hero.badge}
 </div>
 <h1 className="text-5xl md:text-7xl font-bold text-theme-primary leading-[1.1] animate-fade-in-up">
 {siteContent.hero.titleLine1} <br />
 <span className="text-accent italic font-serif">{siteContent.hero.titleAccent}</span>
 </h1>
 <p className="text-lg md:text-xl text-theme-secondary leading-relaxed max-w-xl mx-auto lg:mx-0 animate-fade-in-up delay-200">
 {siteContent.hero.description}
 </p>
 <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-in-up delay-400 justify-center lg:justify-start">
 <Link
 href="/products"
 className="px-10 py-4 bg-accent text-white rounded-lg font-bold hover:bg-accent-hover transition-all shadow-lg hover:shadow-accent/20 flex items-center justify-center group"
 >
 {siteContent.hero.button1}
 <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" width="20" height="20" />
 </Link>
 <Link
 href="/about"
 className="px-10 py-4 border-2 border-accent text-accent rounded-lg font-bold hover:bg-accent-light transition-all flex items-center justify-center"
 >
 {siteContent.hero.button2}
 </Link>
 </div>

 {/* Stats/Proof points */}
 <div className="grid grid-cols-3 gap-8 pt-10 border-t border-theme-border animate-fade-in delay-600">
 {siteContent.stats.map((stat, index) => (
 <div key={index}>
 <div className="text-2xl font-bold text-theme-primary">{stat.value}</div>
 <div className="text-xs text-theme-tertiary uppercase tracking-wider">{stat.label}</div>
 </div>
 ))}
 </div>
 </div>

 {/* Decorative Image with Organic Shape */}
 <div className="relative group animate-fade-in delay-300">
 <div className="relative w-full h-full max-w-2xl mx-auto">
 <div className="aspect-[4/5] relative rounded-t-[200px] overflow-hidden shadow-2xl transition-transform duration-700 group-hover:scale-[1.02]">
 <Image
 src={siteContent.hero?.image || '/hero_custom_boutique.png'}
 alt="Custom Products Boutique"
 fill
 className="object-cover"
 priority
 />
 </div>
 </div>
 {/* Background decorative elements */}
 <div className="absolute -top-10 -right-10 w-64 h-64 bg-accent-light rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-blob"></div>
 <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-[#f4f4f0] rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-blob animation-delay-2000"></div>
 </div>
 </div>
 </div>
 </section>
 )}
 {/* Features Section - Clean Minimalist Style */}
 <section className="py-20 bg-theme-primary relative overflow-hidden">
 {/* Subtle Accent */}
 <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-accent/10 to-transparent pointer-events-none" />
 
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
 <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
 {loadingData ? (
 [...Array(3)].map((_, index) => (
 <FeatureCardSkeleton key={index} />
 ))
 ) : (
 siteContent.features.map((feature, index) => {
 const Icon = featureIcons[index];
 return (
 <div
 key={index}
 className="flex flex-col items-center text-center p-6 group"
 >
 <div className="w-16 h-16 rounded-2xl bg-accent-light flex items-center justify-center mb-6 text-accent transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
 {Icon && <Icon className="w-8 h-8" width="32" height="32" />}
 </div>
 <h2 className="text-xl font-bold text-accent mb-3">
 {feature.title}
 </h2>
 <p className="text-theme-secondary leading-relaxed">
 {feature.description}
 </p>
 </div>
 );
 })
 )}
 </div>
 </div>
 </section>

 {/* Featured Categories - Organic Grid */}
 <section className="py-24 bg-bg-secondary relative">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
 <div className="max-w-2xl">
 <h2 className="text-3xl md:text-5xl font-bold text-theme-primary mb-4 font-serif">
 {siteContent.categories.titlePrefix} <span className="italic text-accent">{siteContent.categories.titleAccent}</span>
 </h2>
 <p className="text-lg text-theme-secondary">
 {siteContent.categories.subtitle}
 </p>
 </div>
 <Link
 href="/products"
 className="text-accent font-bold flex items-center group mb-2"
 >
 Toutes les catégories
 <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
 </Link>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
 {loadingData ? (
 [...Array(4)].map((_, index) => (
 <div key={index} className={index % 3 === 0 ? "md:col-span-8" : "md:col-span-4"}>
 <CategoryCardSkeleton />
 </div>
 ))
 ) : (
 categories.map((category, index) => {
 const isLarge = index % 3 === 0;
 return (
 <div 
 key={category._id} 
 className={`${isLarge ? "md:col-span-8" : "md:col-span-4"} animate-fade-in-up`}
 style={{ animationDelay: `${index * 100}ms` }}
 >
 <CategoryCard category={category} index={index} fallbackImages={galleryFallbacks} />
 </div>
 );
 })
 )}
 </div>
 </div>
 </section>

 {/* Featured Products - High Contrast Gallery */}
 <section className="py-24 bg-theme-primary relative overflow-hidden">
 {/* Decorative Section Background Accents */}
 <div className="absolute top-0 right-0 w-96 h-96 bg-accent-light opacity-30 rounded-full blur-3xl -mr-48 -mt-24 pointer-events-none" />
 <div className="absolute bottom-0 left-0 w-80 h-80 bg-theme-tertiary opacity-40 rounded-full blur-3xl -ml-40 -mb-20 pointer-events-none" />
 
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
 <div className="text-center mb-16">
 <h2 className="text-3xl md:text-5xl font-bold text-theme-primary mb-6 font-serif">
 {siteContent.featured.titlePrefix} <span className="italic text-accent">{siteContent.featured.titleAccent}</span>
 </h2>
 <p className="text-lg text-theme-secondary max-w-2xl mx-auto italic opacity-80">
 {siteContent.featured.description}
 </p>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
 {loadingData ? (
 [...Array(4)].map((_, index) => (
 <ProductCardSkeleton key={index} />
 ))
 ) : (
 featuredProducts.map((product) => (
 <div key={product._id} className="h-full">
 <ProductCard product={product} />
 </div>
 ))
 )}
 </div>

 <div className="text-center mt-16">
 <Link
 href="/products?featured=true"
 className="px-10 py-4 bg-accent text-white rounded-lg font-bold hover:bg-accent-hover transition-all shadow-lg inline-flex items-center group"
 >
 Voir toute la collection 
 <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" width="20" height="20" />
 </Link>
 </div>
 </div>
 </section>
 {/* New Arrivals */}
 {newArrivals.length > 0 && (
 <section className="py-16 md:py-24 bg-theme-primary relative overflow-hidden"> 
 {/* Decorative Accent */}
 <div className="absolute top-1/2 left-0 w-64 h-64 bg-accent/5 rounded-full blur-3xl -ml-32 pointer-events-none" />
 
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
 <div className="text-center mb-12">
 <h2 className="text-3xl md:text-4xl font-bold text-theme-primary mb-4"> 
 Nouveautés 
 </h2>
 <p className="text-lg text-theme-secondary max-w-2xl mx-auto"> 
 Nouveaux arrivages rien que pour vous. Soyez les premiers à obtenir ces nouveaux produits incroyables.
 </p>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
 {loadingData ? (
 [...Array(4)].map((_, index) => (
 <ProductCardSkeleton key={index} />
 ))
 ) : (
 newArrivals.map((product) => (
 <div key={product._id} className="h-full">
 <ProductCard product={{...product, isNew: true}} />
 </div>
 ))
 )}
 </div>

 <div className="text-center mt-12">
 <Link
 href="/products"
 className="btn-secondary inline-flex items-center group"
 >
 Voir tous les produits 
 <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" width="20" height="20" />
 </Link>
 </div>
 </div>
 </section>
 )}
 {/* CTA Section - Sophisticated Impact */}
 <section className="py-24 bg-accent text-white overflow-hidden relative">
 {/* Decorative background circle */}
 <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white opacity-[0.03] rounded-full blur-3xl pointer-events-none"></div>
 
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
 <h2 className="text-4xl md:text-6xl font-bold mb-8 font-serif leading-tight">
 Enrichissez votre <span className="italic opacity-90 underline decoration-white/30 decoration-2 underline-offset-8">collection</span>
 </h2>
 <p className="text-xl text-white/90 mb-12 max-w-2xl mx-auto leading-relaxed"> 
 Rejoignez notre communauté engagée pour un mode de vie plus beau, plus simple et plus responsable.
 </p>
 <div className="flex flex-col sm:flex-row gap-6 justify-center">
 <Link
 href="/products"
 className="px-10 py-4 bg-white text-accent rounded-lg font-bold hover:bg-bg-primary transition-all shadow-xl text-lg"
 >
 Commencer les achats 
 </Link>
 <Link
 href="/contact"
 className="px-10 py-4 border-2 border-white/40 text-white rounded-lg font-bold hover:bg-white/10 transition-all text-lg"
 >
 Nous contacter
 </Link>
 </div>
 </div>
 </section>
 </>
 );
}
